/**
 * Shared Google Ads API client + types.
 *
 * The page-level GoogleAdsPage was the first consumer and still keeps its
 * own copies of these types inline; this module exists so the new Phase-3
 * surfaces (AdsAccountPicker, ClientAdsTab, TrueRoasWidget) don't have to
 * duplicate them again. The plan is to migrate GoogleAdsPage onto this
 * module in a separate commit — not yet, to keep this commit focused.
 */

import { authFetch } from '../../utils/cloudFunctionsAuth';

export const CF_BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';

// ─────────────────────────────────────────────────────────────────────────
// Wire types — mirror Google's REST response shapes
// ─────────────────────────────────────────────────────────────────────────

export interface AccessibleCustomer {
  customerId: string;
  resourceName?: string;
}

/** Enrichment from customer_clients_under_manager. Populated for accounts
 *  that are descendants of the configured MCC; absent for accounts the
 *  user has direct access to that aren't under that manager. */
export interface EnrichedAccount {
  descriptiveName?: string;
  manager?: boolean;
  testAccount?: boolean;
  status?: string;
  currencyCode?: string;
  level?: number;
}

export interface CustomerClientRow {
  customerClient: {
    id: string;
    clientCustomer?: string;
    descriptiveName?: string;
    manager?: boolean;
    testAccount?: boolean;
    status?: string;
    currencyCode?: string;
    timeZone?: string;
    level?: string | number;
  };
}

export interface CustomerSummaryRow {
  customer: {
    id: string;
    descriptiveName?: string;
    currencyCode?: string;
    timeZone?: string;
    status?: string;
    manager?: boolean;
    testAccount?: boolean;
    autoTaggingEnabled?: boolean;
  };
}

export interface CampaignRow {
  campaign: {
    id: string;
    name: string;
    status: string;
    advertisingChannelType: string;
  };
  /** Search impression-share snapshot (fractions 0..1; null = unknown). */
  impressionShare?: { search?: number | null; budgetLost?: number | null; rankLost?: number | null } | null;
  campaignBudget?: { amountMicros?: string };
  metrics?: {
    costMicros?: string;
    impressions?: string;
    clicks?: string;
    conversions?: number;
    conversionsValue?: number;
    ctr?: number;
    averageCpc?: string;
  };
}

export interface DailyPerformanceRow {
  segments: { date: string };
  metrics?: {
    costMicros?: string;
    impressions?: string;
    clicks?: string;
    conversions?: number;
    conversionsValue?: number;
  };
}

export interface QueryResponse<T> {
  ok: boolean;
  rows?: T[];
  error?: string;
  /** Parsed GoogleAdsFailure enum like 'queryError.REQUESTED_METRICS_FOR_MANAGER'
   *  or 'authorizationError.USER_PERMISSION_DENIED'. Consumers branch on
   *  this to handle structural errors specially (e.g. auto-eject from a
   *  sub-manager account). */
  errorCode?: string;
  httpStatus?: number;
}

export interface ConfigResponse {
  ok: boolean;
  loginCustomerId: string | null;
  defaultCustomerId: string | null;
}

export interface AccessibleCustomersResponse {
  ok: boolean;
  customers?: AccessibleCustomer[];
  error?: string;
}

// ─────────────────────────────────────────────────────────────────────────
// API helpers
// ─────────────────────────────────────────────────────────────────────────

/** POST a named GAQL query against a customer id and return typed rows.
 *  Optional loginCustomerId routes the query through a specific ancestor
 *  manager — needed for multi-MCC hierarchies where the configured manager
 *  isn't an ancestor of the target account. */
export async function callAdsQuery<T>(
  customerId: string,
  queryName: string,
  loginCustomerId?: string,
  params?: Record<string, string>,
): Promise<QueryResponse<T>> {
  try {
    const payload: Record<string, unknown> = { customerId, queryName };
    if (loginCustomerId) payload.loginCustomerId = loginCustomerId;
    if (params && Object.keys(params).length > 0) payload.params = params;
    const r = await authFetch(`${CF_BASE}/googleAdsQuery`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const text = await r.text();
    let data: any = null;
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || !data?.ok) {
      return {
        ok: false,
        error: data?.error || `HTTP ${r.status}`,
        errorCode: data?.errorCode,
        httpStatus: r.status,
      };
    }
    return { ok: true, rows: Array.isArray(data.rows) ? data.rows : [] };
  } catch (e: any) {
    return { ok: false, error: e?.message || 'Request failed' };
  }
}

/** Fetch the saved Google Ads integration config (masked secrets +
 *  loginCustomerId / defaultCustomerId). Used by the picker to know
 *  which CID is the manager and which leaf is the user's chosen
 *  default. */
export async function fetchAdsConfig(): Promise<ConfigResponse | null> {
  try {
    const r = await authFetch(`${CF_BASE}/getGoogleAdsConfig`, { method: 'GET' });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

/** Smoke-test + return the user's accessible accounts. Same call the
 *  Settings page uses for its connection test. */
export async function fetchAccessibleCustomers(): Promise<AccessibleCustomersResponse> {
  try {
    const r = await authFetch(`${CF_BASE}/testGoogleAdsConnection`, { method: 'POST' });
    const text = await r.text();
    let data: any = null;
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || !data?.ok) {
      return { ok: false, error: data?.error || `HTTP ${r.status}` };
    }
    return data as AccessibleCustomersResponse;
  } catch (e: any) {
    return { ok: false, error: e?.message || 'Request failed' };
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Account list loader
// ─────────────────────────────────────────────────────────────────────────

export interface AdsAccountListResult {
  /** Union of listAccessibleCustomers + customer_clients descendants
   *  (across ALL discovered managers) — the full picker surface. */
  displayAccounts: AccessibleCustomer[];
  /** Metadata per cid (descriptive_name, manager flag, status, etc.). */
  enriched: Record<string, EnrichedAccount>;
  /** Configured manager (login-customer-id). Always in knownManagers. */
  managerCid: string;
  /** Configured default customer id (or empty). */
  defaultCid: string;
  /** Every cid known to be a manager — seeded from the configured MCC +
   *  multi-manager discovery. */
  knownManagers: Set<string>;
  /** Per-account → the ancestor manager cid to pass as login-customer-id
   *  when querying it. Populated from customer_clients results. Accounts
   *  not under any discovered manager (pure direct access) are absent —
   *  callers omit the override and rely on the backend's retry-without-
   *  login fallback. This is what makes accounts under a DIFFERENT manager
   *  than the configured one queryable. */
  managerOf: Record<string, string>;
  error?: string;
}

/**
 * Load the full picker surface in one call. Combines:
 *   - testGoogleAdsConnection (the directly-accessible list)
 *   - getGoogleAdsConfig (manager + default ids)
 *   - customer_clients_under_manager (the MCC tree)
 *
 * Returns a unified shape suitable for both a one-shot picker (this is
 * Phase 3's `AdsAccountPicker` use case) and the long-running page
 * (`GoogleAdsPage`'s existing logic should migrate onto this helper).
 *
 * Best-effort on partial failure: if the enrichment query fails the
 * function still returns the directly-accessible list with empty
 * enrichment. If the smoke-test itself fails, `error` is set and
 * `displayAccounts` is empty.
 */
export async function loadAdsAccountList(): Promise<AdsAccountListResult> {
  const result: AdsAccountListResult = {
    displayAccounts: [],
    enriched: {},
    managerCid: '',
    defaultCid: '',
    knownManagers: new Set(),
    managerOf: {},
  };

  // Parallel: accessible accounts + config
  const [accessible, config] = await Promise.all([
    fetchAccessibleCustomers(),
    fetchAdsConfig(),
  ]);

  if (!accessible.ok) {
    result.error = accessible.error || 'Could not load accessible accounts';
    return result;
  }

  const accounts = accessible.customers || [];
  result.managerCid = String(config?.loginCustomerId || '');
  result.defaultCid = String(config?.defaultCustomerId || '');
  if (result.managerCid) result.knownManagers.add(result.managerCid);

  // ── Multi-manager hierarchy discovery ───────────────────────────────
  // A customer_clients query against a manager returns its FULL recursive
  // subtree. But the configured manager may be a SUB-manager — accounts
  // under a PARENT or sibling manager won't appear in its subtree, and
  // aren't necessarily in listAccessibleCustomers either. So we discover
  // the wider hierarchy by querying customer_clients against the configured
  // manager first, then against any accessible account NOT yet covered
  // (candidates for being a parent/other manager). Whichever is the top
  // manager contributes the full tree; leaves contribute nothing and are
  // skipped. Bounded by accessible-account count + a hard iteration cap.

  /** Query one manager's subtree and merge into enriched + managerOf.
   *  Returns the descendant cids found plus a `failed` flag so the caller can
   *  tell a genuinely-empty subtree apart from a transient query failure (the
   *  two must NOT be conflated — see the discovery loop). Mirrors the backend
   *  resolveHierarchy.queryTree contract for parity. */
  const queryManagerTree = async (managerId: string): Promise<{ found: string[]; failed: boolean }> => {
    const resp = await callAdsQuery<CustomerClientRow>(
      managerId,
      'customer_clients_under_manager',
      managerId, // login-customer-id = the manager whose tree we're reading
    );
    const found: string[] = [];
    if (!resp.ok || !resp.rows) return { found, failed: !resp.ok };
    for (const row of resp.rows) {
      const cc = row.customerClient;
      if (!cc?.id) continue;
      const cid = String(cc.id);
      const isSelf = cid === managerId;
      // Merge metadata (don't overwrite an existing richer entry with a
      // sparser one — first writer wins on name, but always upgrade manager flag).
      const existing = result.enriched[cid];
      result.enriched[cid] = {
        descriptiveName: existing?.descriptiveName || cc.descriptiveName,
        manager: existing?.manager || cc.manager === true,
        testAccount: existing?.testAccount || cc.testAccount === true,
        status: existing?.status || cc.status,
        currencyCode: existing?.currencyCode || cc.currencyCode,
        level: existing?.level ?? (typeof cc.level === 'string' ? Number(cc.level) : (cc.level as number | undefined)),
      };
      if (cc.manager === true) result.knownManagers.add(cid);
      // Record the ancestor manager to use as login-customer-id for this
      // account — but not for the manager itself (level 0 / self row).
      if (!isSelf && !result.managerOf[cid]) {
        result.managerOf[cid] = managerId;
      }
      found.push(cid);
    }
    return { found, failed: false };
  };

  const queriedManagers = new Set<string>();

  // 1. Configured manager first.
  if (result.managerCid) {
    queriedManagers.add(result.managerCid);
    await queryManagerTree(result.managerCid);
  }

  // 2. Discovery passes: any accessible account not yet covered is a
  //    candidate parent/other manager. Query its tree. Repeat until no
  //    new accounts appear or we hit the iteration cap. Each pass queries
  //    uncovered candidates in parallel.
  // A transient query failure must NOT look like an empty subtree — otherwise a
  // blip silently drops whole account subtrees (and diverges from the backend,
  // causing sync/UI mismatch). Only mark SUCCESSFULLY-queried candidates as
  // queried; retry failed ones next pass. MAX_PASSES is the hard loop guard.
  // Mirrors the backend resolveHierarchy discovery loop for parity.
  const MAX_PASSES = 10;
  for (let pass = 0; pass < MAX_PASSES; pass++) {
    // Array.from instead of spreading the Set — this codebase's TS target
    // predates downlevelIteration, so `[...someSet]` fails to compile.
    const coveredOrQueried = new Set<string>(
      Object.keys(result.enriched).concat(Array.from(queriedManagers)),
    );
    const candidates = accounts
      .map((a) => a.customerId)
      .filter((cid) => cid && !coveredOrQueried.has(cid));
    if (candidates.length === 0) break;

    const results = await Promise.all(candidates.map((cid) => queryManagerTree(cid)));
    // Mark only non-failed candidates as queried; failed ones stay eligible for
    // retry. On the LAST pass mark failed ones too so the loop can't spin.
    const lastPass = pass === MAX_PASSES - 1;
    for (let i = 0; i < candidates.length; i++) {
      if (!results[i].failed || lastPass) queriedManagers.add(candidates[i]);
    }
    const discoveredSomething = results.some((r) => r.found.length > 0);
    const anyFailed = results.some((r) => r.failed);
    // Stop only when nothing new was discovered AND nothing transiently failed
    // (so there's nothing left to retry).
    if (!discoveredSomething && !anyFailed) break;
  }

  // ── Union into displayAccounts ──────────────────────────────────────
  // Directly-accessible first (preserves API order), then any descendants
  // discovered via the manager trees. Dedupe by cid.
  const seen = new Set<string>();
  for (const a of accounts) {
    if (!a.customerId || seen.has(a.customerId)) continue;
    seen.add(a.customerId);
    result.displayAccounts.push(a);
  }
  for (const cid of Object.keys(result.enriched)) {
    if (seen.has(cid)) continue;
    seen.add(cid);
    result.displayAccounts.push({ customerId: cid, resourceName: `customers/${cid}` });
  }

  return result;
}

// ─────────────────────────────────────────────────────────────────────────
// Formatting helpers
// ─────────────────────────────────────────────────────────────────────────

/** Strip dashes from "895-911-3502" → "8959113502". Google Ads APIs
 *  want bare digits in customer-id paths and the login-customer-id
 *  header; the picker stores the raw form too. */
export function normalizeCustomerId(value: string | undefined | null): string {
  return String(value || '').replace(/\D+/g, '');
}

/** Pretty-print a 10-digit customer id as "895-911-3502". */
export function formatCustomerId(id: string | undefined | null): string {
  const digits = normalizeCustomerId(id);
  if (digits.length !== 10) return digits;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Build a human-readable label for a picker row, combining the
 *  descriptive name (if known) and the role (top manager / sub-manager /
 *  test / regular). */
export function describeAccount(
  customerId: string,
  enriched: Record<string, EnrichedAccount>,
  managerCid: string,
  knownManagers: Set<string>,
): string {
  const meta = enriched[customerId];
  const isMgr = knownManagers.has(customerId);
  const name = meta?.descriptiveName;
  const role = isMgr
    ? (customerId === managerCid ? ' · manager (top)' : ' · sub-manager')
    : (meta?.testAccount ? ' · test' : '');
  return `${name ? `${name} · ` : ''}${formatCustomerId(customerId)}${role}`;
}

/** cost_micros → currency. 1,234,567,890 micros = $1,234.57. */
export function microsToAmount(micros: string | number | undefined): number {
  if (micros === undefined || micros === null || micros === '') return 0;
  const num = Number(micros);
  return isFinite(num) ? num / 1_000_000 : 0;
}

// ─────────────────────────────────────────────────────────────────────────
// Timeframe range (shared across the marketing surfaces)
// ─────────────────────────────────────────────────────────────────────────

function n(v: any): number { const x = Number(v); return isFinite(x) ? x : 0; }

export type AdsRangePreset = 'today' | 'yesterday' | '3d' | '7d' | '30d' | '90d' | 'month' | 'custom';

export interface AdsRange {
  preset: AdsRangePreset;
  from: string;   // YYYY-MM-DD inclusive
  to: string;     // YYYY-MM-DD inclusive
  label: string;  // short display label
}

/** Browser-local today as YYYY-MM-DD. The backend re-clamps to the account
 *  timezone + 37-month retention floor, so a browser-vs-account TZ skew at the
 *  day boundary is harmless (shifts "today" by at most a day). */
export function todayLocal(): string {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

export function addDaysStr(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Build a normalized AdsRange for a preset. Inclusive, ending today (so
 *  "last 30 days" includes today's partial data). */
export function presetRange(preset: AdsRangePreset, customFrom?: string, customTo?: string): AdsRange {
  const today = todayLocal();
  switch (preset) {
    case 'today': return { preset, from: today, to: today, label: 'today' };
    case 'yesterday': {
      const y = addDaysStr(today, -1);
      return { preset, from: y, to: y, label: 'yesterday' };
    }
    case '3d':  return { preset, from: addDaysStr(today, -2),  to: today, label: 'last 3 days' };
    case '7d':  return { preset, from: addDaysStr(today, -6),  to: today, label: 'last 7 days' };
    case '90d': return { preset, from: addDaysStr(today, -89), to: today, label: 'last 90 days' };
    case 'month': {
      const first = `${today.slice(0, 7)}-01`;
      return { preset, from: first, to: today, label: 'this month' };
    }
    case 'custom': {
      const from = customFrom && customFrom <= (customTo || today) ? customFrom : addDaysStr(today, -29);
      const to = customTo || today;
      return { preset, from, to, label: `${from} → ${to}` };
    }
    case '30d':
    default:
      return { preset: '30d', from: addDaysStr(today, -29), to: today, label: 'last 30 days' };
  }
}

export const DEFAULT_ADS_RANGE: AdsRange = presetRange('today');

// ── Selection persistence ────────────────────────────────────────────────
// Remember the chosen timeframe across reloads. We store only the preset KEY
// (plus from/to for 'custom'); on load, non-custom presets are re-derived
// relative to the current day, so a saved 'today'/'last 7 days' always means
// today's window — never a stale snapshot of the day it was saved.
const RANGE_STORAGE_KEY = 'gads.selectedRange.v1';

/** Valid stored presets. Derived by ROUND-TRIP rather than hand-listed: a
 *  hand-maintained copy of the preset list silently dropped '3d', so choosing
 *  "last 3 days" wrote fine and was rejected on read, reverting to 'today'
 *  every reload. Anything presetRange() round-trips is by definition storable,
 *  so this can't drift from the real preset set again. */
function isStorablePreset(p: unknown): p is AdsRangePreset {
  if (typeof p !== 'string') return false;
  if (p === 'custom') return true;
  try { return presetRange(p as AdsRangePreset).preset === p; } catch { return false; }
}

export function storeAdsRange(range: AdsRange): void {
  try {
    const payload = range.preset === 'custom'
      ? { preset: 'custom', from: range.from, to: range.to }
      : { preset: range.preset };
    window.localStorage.setItem(RANGE_STORAGE_KEY, JSON.stringify(payload));
  } catch { /* storage unavailable (private mode / quota) — non-fatal */ }
}

export function loadStoredAdsRange(): AdsRange {
  try {
    const raw = window.localStorage.getItem(RANGE_STORAGE_KEY);
    if (!raw) return DEFAULT_ADS_RANGE;
    const saved = JSON.parse(raw) as { preset?: AdsRangePreset; from?: string; to?: string };
    const preset = saved?.preset;
    if (!isStorablePreset(preset)) return DEFAULT_ADS_RANGE;
    return preset === 'custom' ? presetRange('custom', saved.from, saved.to) : presetRange(preset);
  } catch {
    return DEFAULT_ADS_RANGE;
  }
}

/** Inclusive day count of a range. */
export function rangeDays(r: AdsRange): number {
  const a = new Date(`${r.from}T00:00:00Z`).getTime();
  const b = new Date(`${r.to}T00:00:00Z`).getTime();
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
}

/** Fire the owner-gated auto-backfill so the daily store covers [from,to]
 *  (writes only the missing days; recent volatile days always refresh). */
export async function ensureAdsRangeBackfill(
  customerId: string, from: string, to: string, loginCustomerId?: string,
): Promise<{ ok: boolean; error?: string; opsIssued?: number; docsWritten?: number }> {
  try {
    const r = await authFetch(`${CF_BASE}/ensureGoogleAdsRange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(loginCustomerId ? { customerId, from, to, loginCustomerId } : { customerId, from, to }),
    });
    const text = await r.text();
    let data: any = null;
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true, opsIssued: data.opsIssued, docsWritten: data.docsWritten };
  } catch (e: any) {
    return { ok: false, error: e?.message || 'Request failed' };
  }
}

/** Trigger the owner-gated PER-ACCOUNT full-history daily sync (pulls the
 *  complete time-series back to the retention floor into the packed store).
 *  The function may return state:'paused' if it hits its soft deadline; the
 *  caller should re-invoke until state:'done'. */
export async function fullHistorySync(
  customerId: string, loginCustomerId?: string, opts?: { months?: number; cleanup?: boolean },
): Promise<{ ok: boolean; error?: string; state?: string; windowsDone?: number; windowsTotal?: number; dayCellsWritten?: number; opsIssued?: number; legacyDeleted?: number }> {
  try {
    const payload: Record<string, unknown> = { customerId };
    if (loginCustomerId) payload.loginCustomerId = loginCustomerId;
    if (opts?.months) payload.months = opts.months;
    if (opts?.cleanup) payload.cleanup = true;
    const r = await authFetch(`${CF_BASE}/syncGoogleAdsAccountFullHistory`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const text = await r.text();
    let data: any = null;
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true, state: data.state, windowsDone: data.windowsDone, windowsTotal: data.windowsTotal, dayCellsWritten: data.dayCellsWritten, opsIssued: data.opsIssued, legacyDeleted: data.legacyDeleted };
  } catch (e: any) {
    return { ok: false, error: e?.message || 'Request failed' };
  }
}

/** Trigger the owner-gated query health-check (validates every GAQL query the
 *  app depends on against each account; writes settings/google_ads_health). */
export async function runGoogleAdsHealthCheck(): Promise<{ ok: boolean; error?: string; summary?: any }> {
  try {
    const r = await authFetch(`${CF_BASE}/googleAdsHealthCheck`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
    });
    const text = await r.text();
    let data: any = null;
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true, summary: data.summary };
  } catch (e: any) { return { ok: false, error: e?.message || 'Request failed' }; }
}

/** Trigger owner-gated alert detection now (nightly otherwise). Reads only
 *  synced Firestore data — no Google Ads API calls. */
export async function runGoogleAdsAlertDetection(): Promise<{ ok: boolean; error?: string; active?: number; resolved?: number; accounts?: number }> {
  try {
    const r = await authFetch(`${CF_BASE}/detectGoogleAdsAlertsNow`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
    });
    const text = await r.text();
    let data: any = null;
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true, active: data.active, resolved: data.resolved, accounts: data.accounts };
  } catch (e: any) { return { ok: false, error: e?.message || 'Request failed' }; }
}

// ── Google Ads WRITE pipeline (W1: negatives) ────────────────────────────

export interface NegativeProposalItem { scope?: 'campaign' | 'adGroup'; entityId: string; entityLabel?: string; text: string; matchType?: string; }

/** Draft a negative-keyword change set (validated, pending_review). Owner-gated;
 *  applies NOTHING. */
export async function proposeNegatives(
  customerId: string, items: NegativeProposalItem[],
  opts?: { rationale?: string; source?: string; apply?: boolean },
): Promise<{ ok: boolean; error?: string; changeSet?: any; applied?: any }> {
  try {
    const r = await authFetch(`${CF_BASE}/proposeGoogleAdsNegatives`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId, items, rationale: opts?.rationale || '', source: opts?.source || 'manual', apply: opts?.apply === true }),
    });
    const text = await r.text(); let data: any = {};
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true, changeSet: data.changeSet, applied: data.applied };
  } catch (e: any) { return { ok: false, error: e?.message || 'Request failed' }; }
}

/** Campaign wizard: phase 1 — resolve + dry-run + draft (returns the validated
 *  draft + resolved locations for the Review step). */
export async function proposeCampaign(
  customerId: string, spec: any,
): Promise<{ ok: boolean; error?: string; changeSet?: any; locationNotes?: string[] }> {
  try {
    const r = await authFetch(`${CF_BASE}/proposeGoogleAdsCampaign`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId, spec }),
    });
    const text = await r.text(); let data: any = {};
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true, changeSet: data.changeSet, locationNotes: data.locationNotes };
  } catch (e: any) { return { ok: false, error: e?.message || 'Request failed' }; }
}

/** Campaign wizard: phase 2 — apply a wizard draft (the Review step is the gate). */
export async function applyCampaignDraft(
  customerId: string, changeSetId: string,
): Promise<{ ok: boolean; error?: string; applied?: any }> {
  try {
    const r = await authFetch(`${CF_BASE}/proposeGoogleAdsCampaign`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId, changeSetId, apply: true }),
    });
    const text = await r.text(); let data: any = {};
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true, applied: data.applied };
  } catch (e: any) { return { ok: false, error: e?.message || 'Request failed' }; }
}

/** Draft/apply a generic change set of typed ops (pause/enable, budget,
 *  negatives add/remove). apply:true = user-direct (confirm modal is the gate). */
export async function proposeChange(
  customerId: string, ops: any[],
  opts?: { rationale?: string; source?: string; apply?: boolean; kind?: string },
): Promise<{ ok: boolean; error?: string; changeSet?: any; applied?: any }> {
  try {
    const r = await authFetch(`${CF_BASE}/proposeGoogleAdsChange`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId, ops, rationale: opts?.rationale || '', source: opts?.source || 'manual', apply: opts?.apply === true, kind: opts?.kind || '' }),
    });
    const text = await r.text(); let data: any = {};
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true, changeSet: data.changeSet, applied: data.applied };
  } catch (e: any) { return { ok: false, error: e?.message || 'Request failed' }; }
}

/** Apply / reject / revert a change set (owner-gated). */
export async function decideChangeSet(
  changeSetId: string, action: 'apply' | 'reject' | 'revert', note?: string,
): Promise<{ ok: boolean; error?: string; status?: string }> {
  try {
    const r = await authFetch(`${CF_BASE}/decideGoogleAdsChangeSet`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ changeSetId, action, note: note || '' }),
    });
    const text = await r.text(); let data: any = {};
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true, status: data.status };
  } catch (e: any) { return { ok: false, error: e?.message || 'Request failed' }; }
}

/** Arm / disarm the master write kill switch (owner-gated). */
export async function setWritesEnabled(enabled: boolean): Promise<{ ok: boolean; error?: string }> {
  try {
    const r = await authFetch(`${CF_BASE}/setGoogleAdsWritesEnabled`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled }),
    });
    const text = await r.text(); let data: any = {};
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true };
  } catch (e: any) { return { ok: false, error: e?.message || 'Request failed' }; }
}

// ── Agent access broker (owner-gated management) ───────────────────────────

/** Read the connect code + limits (auto-provisions a code on first read). */
export async function getAgentAccessConfig(): Promise<{ ok: boolean; connectCode?: string; maxPendingGrants?: number; error?: string }> {
  try {
    const r = await authFetch(`${CF_BASE}/getAgentAccessConfig`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true, connectCode: data.connectCode, maxPendingGrants: data.maxPendingGrants };
  } catch (e: any) { return { ok: false, error: e?.message || 'Request failed' }; }
}

/** List all agent grants (owner-gated — reads via requireOwner, not Firestore). */
export async function listAgentGrants(): Promise<{ ok: boolean; grants?: any[]; error?: string }> {
  try {
    const r = await authFetch(`${CF_BASE}/listAgentGrants`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true, grants: Array.isArray(data.grants) ? data.grants : [] };
  } catch (e: any) { return { ok: false, error: e?.message || 'Request failed' }; }
}

/** Mint a new connect code (immediately invalidates the old one). */
export async function rotateAgentConnectCode(): Promise<{ ok: boolean; connectCode?: string; error?: string }> {
  try {
    const r = await authFetch(`${CF_BASE}/rotateAgentConnectCode`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true, connectCode: data.connectCode };
  } catch (e: any) { return { ok: false, error: e?.message || 'Request failed' }; }
}

/** Owner-created agent: mints a PENDING grant + returns its secret ONCE. The
 *  owner then scopes + approves it like any grant and pastes the MCP command —
 *  the agent never handles a connect code or secret exchange itself. */
export async function createAgentGrant(
  label: string, boundCustomerId?: string,
): Promise<{ ok: boolean; agentUid?: string; agentSecret?: string; mcpUrl?: string; error?: string }> {
  try {
    const r = await authFetch(`${CF_BASE}/createAgentGrant`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label, boundCustomerId: boundCustomerId || '' }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true, agentUid: data.agentUid, agentSecret: data.agentSecret, mcpUrl: data.mcpUrl };
  } catch (e: any) { return { ok: false, error: e?.message || 'Request failed' }; }
}

/** Approve / extend / update / revoke / delete an agent access grant. 'update'
 *  edits a grant in place WITHOUT touching its status or time window (rename,
 *  client, scope, capabilities, account / hosting scope) and works on a grant in
 *  any state. */
export async function decideAgentAccess(
  agentUid: string,
  action: 'approve' | 'extend' | 'update' | 'revoke' | 'delete',
  opts?: { ttlHours?: number; label?: string; scope?: 'read' | 'read_propose' | 'none'; allowedCustomerIds?: string[]; capabilities?: string[]; boundCustomerId?: string; allowedHostingIds?: string[]; hostingAllAccounts?: boolean },
): Promise<{ ok: boolean; status?: string; error?: string }> {
  try {
    const r = await authFetch(`${CF_BASE}/decideAgentAccess`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agentUid, action, ...(opts || {}) }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true, status: data.status };
  } catch (e: any) { return { ok: false, error: e?.message || 'Request failed' }; }
}

// ── OAuth reconnect ────────────────────────────────────────────────────────

/** True when an error message signals a credential/OAuth problem that a
 *  reconnect would fix — including the live token-refresh failures Google
 *  returns ('invalid_grant', 'Token has been expired or revoked', the
 *  backend's 'Refresh failed: …'). Deliberately broad on the auth axis only;
 *  a bare 403 (which the Ads API returns for many non-auth reasons) is NOT
 *  treated as auth trouble. */
export function looksLikeAdsAuthError(msg: string | null | undefined): boolean {
  return /unauthor|credential|refresh.?token|invalid.?token|invalid_grant|revoked|expired|refresh\s*failed|re-?auth/i
    .test(String(msg || ''));
}

/** Run the Google Ads OAuth reconnect popup: mint a consent URL, open the
 *  popup, and resolve when the callback posts back (or the popup closes). On
 *  success a fresh refresh token has been persisted server-side. Mirrors the
 *  Settings page's connect flow so the marketing page can offer an inline
 *  "Reconnect" without sending the user to Settings. */
export async function startGoogleAdsReconnect(): Promise<{ ok: boolean; error?: string }> {
  try {
    const r = await authFetch(`${CF_BASE}/startGoogleAdsOAuth`, { method: 'GET' });
    const data = await r.json().catch(() => ({}));
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    const consentUrl = String(data.consentUrl || '');
    if (!consentUrl) return { ok: false, error: 'No consent URL returned.' };

    const w = 520, h = 640;
    const left = (window.screen.width - w) / 2;
    const top = (window.screen.height - h) / 2;
    const popup = window.open(consentUrl, 'google-ads-oauth', `width=${w},height=${h},left=${left},top=${top}`);
    if (!popup) return { ok: false, error: 'Popup blocked — allow popups for this site and try again.' };

    await new Promise<void>((resolve, reject) => {
      const onMessage = (ev: MessageEvent) => {
        const d: any = ev?.data;
        if (!d || d.source !== 'google-ads-oauth') return;
        window.removeEventListener('message', onMessage);
        window.clearInterval(poll);
        if (d.ok) resolve(); else reject(new Error(d.message || 'OAuth failed'));
      };
      window.addEventListener('message', onMessage);
      // Belt-and-braces: if the postMessage is missed (popup-blocker policies
      // vary), treat the popup closing as done and let the caller re-test.
      const poll = window.setInterval(() => {
        if (popup.closed) { window.clearInterval(poll); window.removeEventListener('message', onMessage); resolve(); }
      }, 800);
    });
    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: e?.message || 'Reconnect failed' };
  }
}

// ── Autonomous Ads monitors (Wave 3 P1) ───────────────────────────────────
// Backend-written config (firestore.rules forbids client writes). All mutations
// go through the owner-gated manageAdsMonitors / runAdsMonitorsNow endpoints.

export interface AdsMonitor {
  id: string;
  accountId: string;
  accountName?: string;
  watchFor: string[];
  cadence: 'daily' | 'weekly';
  mayProposeFixes: boolean;
  maxProposalsPerRun?: number;
  enabled: boolean;
  createdBy?: string;
  lastRunAt?: { seconds: number; nanoseconds: number } | null;
}

/** List all configured monitors (owner-gated). */
export async function listAdsMonitors(): Promise<{ ok: boolean; monitors?: AdsMonitor[]; error?: string }> {
  try {
    const r = await authFetch(`${CF_BASE}/manageAdsMonitors`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'list' }),
    });
    const text = await r.text(); let data: any = {};
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true, monitors: data.monitors || [] };
  } catch (e: any) { return { ok: false, error: e?.message || 'Request failed' }; }
}

/** Create or update a monitor (owner-gated). Pass `id` to update in place. */
export async function upsertAdsMonitor(monitor: Partial<AdsMonitor>): Promise<{ ok: boolean; monitor?: AdsMonitor; error?: string }> {
  try {
    const r = await authFetch(`${CF_BASE}/manageAdsMonitors`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'upsert', monitor }),
    });
    const text = await r.text(); let data: any = {};
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true, monitor: data.monitor };
  } catch (e: any) { return { ok: false, error: e?.message || 'Request failed' }; }
}

/** Delete a monitor (owner-gated). */
export async function deleteAdsMonitor(id: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const r = await authFetch(`${CF_BASE}/manageAdsMonitors`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'delete', id }),
    });
    const text = await r.text(); let data: any = {};
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true };
  } catch (e: any) { return { ok: false, error: e?.message || 'Request failed' }; }
}

/** Run one monitor immediately (owner-gated). Returns its run summary. */
export async function runAdsMonitorNow(monitorId: string): Promise<{ ok: boolean; findings?: number; proposed?: number; error?: string }> {
  try {
    const r = await authFetch(`${CF_BASE}/runAdsMonitorsNow`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ monitorId }),
    });
    const text = await r.text(); let data: any = {};
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || !data?.ok) return { ok: false, error: data?.error || `HTTP ${r.status}` };
    return { ok: true, findings: data.findings, proposed: data.proposed };
  } catch (e: any) { return { ok: false, error: e?.message || 'Request failed' }; }
}

// ── Packed daily-store helpers (mirror of functions dailyPack.ts) ─────────
// The daily store keeps one doc per (entity, month) with a `days` map keyed by
// day-of-month. Reads query by the `month` field then unpack + clamp to the
// window. Keep in sync with functions/src/services/googleAds/dailyPack.ts.

/** Inclusive 'YYYY-MM' months spanned by [from,to] (YYYY-MM-DD). [] if from>to. */
export function monthsBetween(from: string, to: string): string[] {
  if (!from || !to || from > to) return [];
  const out: string[] = [];
  let y = parseInt(from.slice(0, 4), 10);
  let m = parseInt(from.slice(5, 7), 10);
  const ey = parseInt(to.slice(0, 4), 10);
  const em = parseInt(to.slice(5, 7), 10);
  let guard = 0;
  while ((y < ey || (y === ey && m <= em)) && guard < 1200) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m += 1; if (m > 12) { m = 1; y += 1; }
    guard += 1;
  }
  return out;
}

/** Expand a packed month doc into flat per-day rows, CLAMPED to [from,to].
 *  The clamp is load-bearing (the month-range query returns whole months,
 *  including out-of-window days). */
export function unpackMonthDoc(docData: any, from: string, to: string): any[] {
  if (!docData || typeof docData !== 'object') return [];
  const days = docData.days;
  const month = typeof docData.month === 'string' ? docData.month : '';
  if (!month || !days || typeof days !== 'object') return [];
  const meta: Record<string, any> = {};
  for (const k of ['accountId', 'campaignId', 'campaignName', 'adGroupId', 'adGroupName', 'status', 'channelType']) {
    if (docData[k] !== undefined) meta[k] = docData[k];
  }
  const out: any[] = [];
  for (const dd of Object.keys(days)) {
    const date = `${month}-${dd}`;
    if (date < from || date > to) continue;
    out.push({ ...meta, ...(days[dd] || {}), date });
  }
  return out;
}

// ── Stored daily doc shapes + aggregation ────────────────────────────────

export interface StoredDailyRow {
  date?: string;
  cost?: number; impressions?: number; clicks?: number;
  conversions?: number; conversionsValue?: number;
  campaignId?: string; campaignName?: string;
  adGroupId?: string; adGroupName?: string;
  status?: string; channelType?: string;
}

export interface MetricTotals {
  cost: number; impressions: number; clicks: number;
  conversions: number; conversionsValue: number; ctr: number; avgCpc: number;
}

/** Sum stored daily rows into a bucket (ctr/avgCpc derived, matching the
 *  backend deriveBucket). */
export function aggregateDaily(rows: StoredDailyRow[]): MetricTotals {
  let cost = 0, impressions = 0, clicks = 0, conversions = 0, conversionsValue = 0;
  for (const r of rows) {
    cost += n(r.cost); impressions += n(r.impressions); clicks += n(r.clicks);
    conversions += n(r.conversions); conversionsValue += n(r.conversionsValue);
  }
  return {
    cost, impressions, clicks, conversions, conversionsValue,
    ctr: impressions > 0 ? clicks / impressions : 0,
    avgCpc: clicks > 0 ? cost / clicks : 0,
  };
}
